import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { initAnalytics } from "./helpers/analytics.helper";
import {
  BrowserRouter as Router,
  Routes,
  Route,
} from "react-router-dom";
import { CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { createAppTheme } from "./theme/createAppTheme";
import { useAuthStore } from "./stores/auth.store";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ProtectedLayout from "./components/ProtectedLayout";
import RouteLoadingFallback from "./components/RouteLoadingFallback";
import PermissionDeniedOverlay from "./components/PermissionDeniedOverlay";
import AuthRoute from "./components/AuthRoute";
import HomeRedirect from "./components/HomeRedirect";
import {
  PERM_APPROVAL_LIST,
  PERM_FLOW_STEP_TYPE_LIST,
  PERM_VERIFICATION_BATCH_LIST,
  PERM_VERIFICATION_BATCH_READ,
  PERM_VERIFICATION_CALCULATION_RUN_READ,
  PERM_ESG_EMISSION_FACTOR_LIST,
  PERM_ESG_UTILITY_FACTOR_LIST,
  PERM_ESG_MRV_LIST,
  PERM_ESG_MRV_READ,
  PERM_WASTE_CATALOG_LIST,
  PERM_ORG_CREATE,
  PERM_ADMIN_PRE_ORDER_LIST,
  PERM_ADMIN_USER_LIST,
  PERM_PRE_ORDER_LIST,
  PERM_USER_LIST,
  PERM_API_KEY_LIST,
  PERM_NOTIFICATION_SEND,
  PERM_ADMIN_PICKUP_CREATE_HISTORICAL,
  PERM_DEVICE_LIST,
  PERM_DEVICE_READ,
  PERM_DEVICE_TYPE_LIST,
  USER_MANAGEMENT_ROLES,
} from "./auth";
import HelpI18nProvider from "./components/help/HelpI18nProvider";
import "./i18n/help";

// Pages load lazily; the Suspense boundary lives in ProtectedLayout (and around Login).
const Login = lazy(() => import("./pages/Login"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Organizations = lazy(() => import("./pages/Organizations"));
const OrganizationForm = lazy(() => import("./pages/organizations/OrganizationForm"));
const OrganizationDetail = lazy(() => import("./pages/organizations/OrganizationDetail"));
const Users = lazy(() => import("./pages/Users"));
const UserEdit = lazy(() => import("./pages/UserEdit"));
const ApprovalsInbox = lazy(() => import("./pages/approvals/ApprovalsInbox"));
const VerificationBatches = lazy(() => import("./pages/verification/VerificationBatches"));
const VerificationBatchDetail = lazy(() => import("./pages/verification/VerificationBatchDetail"));
const VerificationCalculationRunDetail = lazy(
  () => import("./pages/verification/VerificationCalculationRunDetail"),
);
const ApiKeysPage = lazy(() => import("./pages/api_keys/ApiKeysPage"));
const SendNotificationPage = lazy(() => import("./pages/notifications/SendNotificationPage"));
const ApiKeyActivityPage = lazy(() => import("./pages/api_keys/ApiKeyActivityPage"));
const SupplierOutletLinksPage = lazy(() => import("./pages/suppliers/SupplierOutletLinksPage"));
const SupplierOutletDetailPage = lazy(() => import("./pages/suppliers/SupplierOutletDetailPage"));
const FacilityLinksPage = lazy(() => import("./pages/facilities/FacilityLinksPage"));

// Devices
const DevicesPage = lazy(() => import("./pages/devices/DevicesPage"));
const DeviceTypesPage = lazy(() => import("./pages/devices/DeviceTypesPage"));
const DeviceDetailPage = lazy(() => import("./pages/devices/DeviceDetailPage"));

// ESG
const EmissionFactorLibrary = lazy(() => import("./pages/esg/EmissionFactorLibrary"));
const EmissionFactorDetail = lazy(() => import("./pages/esg/EmissionFactorDetail"));
const UtilityEmissionFactors = lazy(() => import("./pages/esg/UtilityEmissionFactors"));
const MrvMethodologyLibrary = lazy(() => import("./pages/esg/MrvMethodologyLibrary"));
const MrvMethodologyDetail = lazy(() => import("./pages/esg/MrvMethodologyDetail"));
const MrvMethodologySheetView = lazy(
  () => import("./pages/esg/MrvMethodologySheetView"),
);

// Orders
const PickupOrdersActive = lazy(() => import("./pages/pickup_orders/PickupOrdersActive"));
const PickupOrdersCreate = lazy(() => import("./pages/pickup_orders/PickupOrdersCreate"));
const PickupOrdersRegularCreate = lazy(
  () => import("./pages/pickup_orders/PickupOrdersRegularCreate"),
);
const PickupOrdersHistoricalCreate = lazy(
  () => import("./pages/pickup_orders/PickupOrdersHistoricalCreate"),
);
const PickupOrderCompletePage = lazy(
  () => import("./pages/pickup_orders/PickupOrderCompletePage"),
);
const PickupOrderDetail = lazy(() => import("./pages/pickup_orders/PickupOrderDetail"));
const BatchCompleteSelectPage = lazy(
  () => import("./pages/pickup_orders/BatchCompleteSelectPage"),
);

// Transportation
const FleetManagement = lazy(() => import("./pages/transportation/FleetManagement"));
const Drivers = lazy(() => import("./pages/transportation/Drivers"));
const DriverDetail = lazy(() => import("./pages/transportation/DriverDetail"));
const TruckTracking = lazy(() => import("./pages/transportation/TruckTracking"));
const TruckTrackingDetail = lazy(
  () => import("./pages/transportation/TruckTrackingDetail"),
);
const Deliveries = lazy(() => import("./pages/Deliveries"));
const DeliveryDetail = lazy(() => import("./pages/DeliveryDetail"));
const BatchCompletePage = lazy(() => import("./pages/deliveries/BatchCompletePage"));
const AggregationBatchesPage = lazy(
  () => import("./pages/deliveries/AggregationBatchesPage"),
);

// Recycling
const MaterialTypes = lazy(() => import("./pages/recycling/MaterialTypes"));
const WasteCatalogHub = lazy(() => import("./pages/waste_catalog/WasteCatalogHub"));
const FlowStepCatalogue = lazy(() => import("./pages/operations/FlowStepCatalogue"));

// Containers
const ContainerList = lazy(() => import("./pages/containers/ContainerList"));
const CollectionSites = lazy(() => import("./pages/containers/CollectionSites"));

// Market and invoices
const Orders = lazy(() => import("./pages/Orders"));
const PreOrders = lazy(() => import("./pages/PreOrders"));
const InvoicesPage = lazy(() => import("./pages/invoices/Invoices"));

// Operations (internal admin)
const WasteMoversOpsList = lazy(() => import("./pages/operations/WasteMoversOpsList"));
const WasteMoverOpsDetail = lazy(() => import("./pages/operations/WasteMoverOpsDetail"));
const WasteOutletsOpsList = lazy(() => import("./pages/operations/WasteOutletsOpsList"));
const WasteOutletOpsDetail = lazy(() => import("./pages/operations/WasteOutletOpsDetail"));
const RecyclersOpsList = lazy(() => import("./pages/operations/RecyclersOpsList"));
const FinancePage = lazy(() => import("./pages/finance/FinancePage"));
const RecyclerFinanceDetail = lazy(() => import("./pages/finance/RecyclerFinanceDetail"));
const WasteOutletFinanceDetail = lazy(() => import("./pages/finance/WasteOutletFinanceDetail"));
const RecyclerOpsDetail = lazy(() => import("./pages/operations/RecyclerOpsDetail"));
const KnowledgeBase = lazy(() => import("./pages/support/KnowledgeBase"));
const KnowledgeBaseArticle = lazy(() => import("./pages/support/KnowledgeBaseArticle"));

const queryClient = new QueryClient();

function App() {
  const initAuth = useAuthStore((state) => state.initAuth);
  const [mode, setMode] = useState<"light" | "dark">(() => {
    const savedMode = localStorage.getItem("themeMode");
    return (
      (savedMode as "light" | "dark") ||
      (window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light")
    );
  });

  const toggleTheme = useCallback(() => {
    setMode((prevMode) => {
      const newMode = prevMode === "light" ? "dark" : "light";
      localStorage.setItem("themeMode", newMode);
      return newMode;
    });
  }, []);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  useEffect(() => {
    document.documentElement.dataset.colorMode = mode;
  }, [mode]);

  useEffect(() => {
    initAnalytics();
    initAuth();
  }, [initAuth]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
        <CssBaseline />
        <PermissionDeniedOverlay />
        <Router future={{ v7_startTransition: true }}>
          <Routes>
            <Route
              path="/login"
              element={
                <Suspense fallback={<RouteLoadingFallback />}>
                  <Login />
                </Suspense>
              }
            />
            <Route element={<ProtectedLayout onThemeToggle={toggleTheme} />}>
              <Route
                path="/"
                element={
                  <HomeRedirect>
                    <Dashboard />
                  </HomeRedirect>
                }
              />
              <Route path="/organizations" element={<Organizations />} />
              <Route
                path="/organizations/new"
                element={
                  <AuthRoute requiredPermission={PERM_ORG_CREATE}>
                    <OrganizationForm />
                  </AuthRoute>
                }
              />
              <Route path="/organizations/:organizationId/edit" element={<OrganizationForm />} />
              <Route path="/organizations/:organizationId" element={<OrganizationDetail />} />
              <Route
                path="/users"
                element={
                  <AuthRoute
                    requiredPermission={PERM_ADMIN_USER_LIST}
                    orgScopedPermission={PERM_USER_LIST}
                    legacyRoles={USER_MANAGEMENT_ROLES}
                  >
                    <Users />
                  </AuthRoute>
                }
              />
              <Route
                path="/api-keys"
                element={
                  <AuthRoute requiredPermission={PERM_API_KEY_LIST}>
                    <ApiKeysPage />
                  </AuthRoute>
                }
              />
              <Route
                path="/api-keys/:apiKeyId/activity"
                element={
                  <AuthRoute requiredPermission={PERM_API_KEY_LIST}>
                    <ApiKeyActivityPage />
                  </AuthRoute>
                }
              />
              <Route
                path="/notifications/send"
                element={
                  <AuthRoute requiredPermission={PERM_NOTIFICATION_SEND}>
                    <SendNotificationPage />
                  </AuthRoute>
                }
              />
              <Route
                path="/approvals"
                element={
                  <AuthRoute requiredPermission={PERM_APPROVAL_LIST}>
                    <ApprovalsInbox />
                  </AuthRoute>
                }
              />
              <Route
                path="/verification/batches"
                element={
                  <AuthRoute requiredPermission={PERM_VERIFICATION_BATCH_LIST}>
                    <VerificationBatches />
                  </AuthRoute>
                }
              />
              <Route
                path="/verification/batches/:batchId"
                element={
                  <AuthRoute requiredPermission={PERM_VERIFICATION_BATCH_READ}>
                    <VerificationBatchDetail />
                  </AuthRoute>
                }
              />
              <Route
                path="/verification/calculation-runs/:runId"
                element={
                  <AuthRoute requiredPermission={PERM_VERIFICATION_CALCULATION_RUN_READ}>
                    <VerificationCalculationRunDetail />
                  </AuthRoute>
                }
              />
              <Route path="/finance" element={<FinancePage />} />
              <Route path="/finance/recyclers/:organizationId" element={<RecyclerFinanceDetail />} />
              <Route path="/finance/waste-outlets/:organizationId" element={<WasteOutletFinanceDetail />} />
              <Route path="/users/new" element={<UserEdit />} />
              <Route path="/users/:userId/edit" element={<UserEdit />} />
              <Route
                path="/esg/mrv-methodologies"
                element={
                  <AuthRoute requiredPermission={PERM_ESG_MRV_LIST}>
                    <MrvMethodologyLibrary />
                  </AuthRoute>
                }
              />
              <Route
                path="/esg/mrv-methodologies/:id"
                element={
                  <AuthRoute requiredPermission={PERM_ESG_MRV_READ}>
                    <MrvMethodologyDetail />
                  </AuthRoute>
                }
              />
              <Route
                path="/esg/mrv-methodologies/:id/sheet-view"
                element={
                  <AuthRoute requiredPermission={PERM_ESG_MRV_READ}>
                    <MrvMethodologySheetView />
                  </AuthRoute>
                }
              />
              <Route
                path="/esg/emission-factors"
                element={
                  <AuthRoute requiredPermission={PERM_ESG_EMISSION_FACTOR_LIST}>
                    <EmissionFactorLibrary />
                  </AuthRoute>
                }
              />
              <Route
                path="/esg/emission-factors/:emissionFactorId"
                element={
                  <AuthRoute requiredPermission={PERM_ESG_EMISSION_FACTOR_LIST}>
                    <EmissionFactorDetail />
                  </AuthRoute>
                }
              />
              <Route
                path="/esg/utility-emission-factors"
                element={
                  <AuthRoute requiredPermission={PERM_ESG_UTILITY_FACTOR_LIST}>
                    <UtilityEmissionFactors />
                  </AuthRoute>
                }
              />
              <Route path="/operations/waste-movers" element={<WasteMoversOpsList />} />
              <Route path="/operations/waste-movers/:organizationId" element={<WasteMoverOpsDetail />} />
              <Route path="/operations/waste-outlets" element={<WasteOutletsOpsList />} />
              <Route path="/operations/supplier-links" element={<SupplierOutletLinksPage />} />
              <Route path="/operations/supplier-links/:outletOrgId" element={<SupplierOutletDetailPage />} />
              <Route path="/operations/facility-links" element={<FacilityLinksPage />} />
              <Route path="/operations/waste-outlets/:organizationId" element={<WasteOutletOpsDetail />} />
              <Route path="/operations/recyclers" element={<RecyclersOpsList />} />
              <Route path="/operations/recyclers/:organizationId" element={<RecyclerOpsDetail />} />
              {/* Orders Routes */}
              <Route path="/pickup_orders" element={<PickupOrdersActive />} />
              <Route path="/pickup_orders/regular/create" element={<PickupOrdersRegularCreate />} />
              <Route path="/pickup_orders/create" element={<PickupOrdersCreate />} />
              <Route path="/pickup_orders/batch-complete" element={<BatchCompleteSelectPage />} />
              <Route path="/pickup_orders/:orderId/edit" element={<PickupOrdersCreate />} />
              <Route path="/pickup_orders/:orderId/complete" element={<PickupOrderCompletePage />} />
              <Route path="/pickup_orders/:orderId" element={<PickupOrderDetail />} />
              <Route
                path="/pickup_orders/historical/create"
                element={
                  <AuthRoute requiredPermission={PERM_ADMIN_PICKUP_CREATE_HISTORICAL}>
                    <PickupOrdersHistoricalCreate />
                  </AuthRoute>
                }
              />
              {/* Transportation Routes */}
              <Route path="/transportation/fleet" element={<FleetManagement />} />
              <Route path="/transportation/drivers" element={<Drivers />} />
              <Route path="/transportation/drivers/:driverId" element={<DriverDetail />} />
              <Route path="/deliveries" element={<Deliveries />} />
              <Route path="/deliveries/aggregations" element={<AggregationBatchesPage />} />
              <Route path="/deliveries/aggregations/:aggregationKey/complete" element={<BatchCompletePage />} />
              <Route path="/deliveries/:deliveryId" element={<DeliveryDetail />} />
              <Route path="/truck_tracking" element={<TruckTracking />} />
              <Route path="/truck_tracking/:truckId" element={<TruckTrackingDetail />} />
              <Route
                path="/operations/flow-steps"
                element={
                  <AuthRoute requiredPermission={PERM_FLOW_STEP_TYPE_LIST}>
                    <FlowStepCatalogue />
                  </AuthRoute>
                }
              />
              {/* Recycling Routes */}
              <Route
                path="/recycling/waste-types"
                element={
                  <AuthRoute requiredPermission={PERM_WASTE_CATALOG_LIST}>
                    <WasteCatalogHub />
                  </AuthRoute>
                }
              />
              <Route path="/recycling/materials" element={<MaterialTypes />} />
              {/* Container Routes */}
              <Route path="/containers/list" element={<ContainerList />} />
              <Route path="/containers/sites" element={<CollectionSites />} />
              <Route
                path="/devices"
                element={
                  <AuthRoute requiredPermission={PERM_DEVICE_LIST}>
                    <DevicesPage />
                  </AuthRoute>
                }
              />
              <Route
                path="/devices/types"
                element={
                  <AuthRoute requiredPermission={PERM_DEVICE_TYPE_LIST}>
                    <DeviceTypesPage />
                  </AuthRoute>
                }
              />
              <Route
                path="/devices/:id"
                element={
                  <AuthRoute requiredPermission={PERM_DEVICE_READ}>
                    <DeviceDetailPage />
                  </AuthRoute>
                }
              />
              <Route path="/orders" element={<Orders />} />
              <Route
                path="/pre_orders"
                element={
                  <AuthRoute
                    requiredPermission={PERM_ADMIN_PRE_ORDER_LIST}
                    orgScopedPermission={PERM_PRE_ORDER_LIST}
                  >
                    <PreOrders />
                  </AuthRoute>
                }
              />
              <Route path="/invoices" element={<InvoicesPage />} />
              <Route
                path="/knowledge-base"
                element={
                  <HelpI18nProvider>
                    <KnowledgeBase />
                  </HelpI18nProvider>
                }
              />
              <Route
                path="/knowledge-base/:articleId"
                element={
                  <HelpI18nProvider>
                    <KnowledgeBaseArticle />
                  </HelpI18nProvider>
                }
              />
            </Route>
          </Routes>
        </Router>
        </LocalizationProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
